import { useState } from 'react';
import { ArrowLeft, Mail, X } from 'lucide-react';
import { AuthInput } from './AuthInput';
import { Button } from './Button';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';

/**
 * LoginScreen - Pantalla de inicio de sesión
 * 
 * REFACTORIZADO:
 * - Ya no recibe callbacks como props
 * - Usa hooks para autenticación y navegación
 */
export const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Hooks de servicios
  const { login } = useAuth();
  const { goBack, goToHome } = useNavigation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password) {
      // Login y navegación automática
      login({
        id: '1',
        name: 'Usuario',
        email: email,
      });
      goToHome();
    }
  };

  const handleGoogleLogin = () => {
    // Simular login con Google
    login({
      id: '1',
      name: 'Usuario Google',
      email: 'usuario@gmail.com',
    });
    goToHome();
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] lg:flex lg:items-center lg:justify-center">
      {/* Mobile View */}
      <div className="lg:hidden min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] z-10">
          <div className="flex items-center gap-3 p-4">
            {goBack && (
              <button
                onClick={goBack}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors"
                aria-label="Volver"
              >
                <ArrowLeft className="w-6 h-6 text-[#111827]" />
              </button>
            )}
            <h2 className="text-base font-semibold text-[#111827]">Iniciar sesión</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-8">
          {/* Logo & Title */}
          <div className="mb-8">
            <div className="w-14 h-14 bg-[#FF6A2A] rounded-[14px] flex items-center justify-center mb-4">
              <span className="text-white font-bold text-2xl">M</span>
            </div>
            <h1 className="text-2xl font-bold text-[#111827] mb-2">
              Inicia sesión
            </h1>
            <p className="text-[#6B7280]">
              Accede a tu cuenta de Mexmo
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <AuthInput
              type="email"
              label="Correo electrónico"
              placeholder="tu@email.com"
              value={email}
              onChange={setEmail}
              icon={<Mail className="w-5 h-5" />}
            />

            <AuthInput
              type="password"
              label="Contraseña"
              placeholder="Ingresa tu contraseña"
              value={password}
              onChange={setPassword}
            />

            {/* Forgot Password */}
            <div className="text-right">
              <button
                type="button"
                className="text-sm font-semibold text-[#FF6A2A] hover:text-[#C2410C] transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
            >
              Iniciar sesión
            </Button>

            {/* Divider */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E5E7EB]"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-[#6B7280]">
                  o continúa con
                </span>
              </div>
            </div>

            {/* Google Button */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-[14px] border border-[#E5E7EB] bg-white hover:bg-[#F7F8FA] transition-colors font-semibold text-[#111827]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continuar con Google
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center mt-6">
            <span className="text-[#6B7280]">¿No tienes cuenta? </span>
            <button className="font-semibold text-[#FF6A2A] hover:text-[#C2410C] transition-colors">
              Crear cuenta
            </button>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:flex lg:w-full lg:max-w-6xl lg:bg-white lg:rounded-[24px] lg:shadow-xl lg:overflow-hidden lg:min-h-[600px] relative">
        {/* Close Button (Desktop) */}
        {goBack && (
          <button
            onClick={goBack}
            className="absolute top-6 right-6 z-20 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors group"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 text-[#6B7280] group-hover:text-[#111827]" />
          </button>
        )}
        
        {/* Left Side - Image/Illustration */}
        <div className="w-1/2 bg-gradient-to-br from-[#FF6A2A] to-[#C2410C] p-12 flex flex-col justify-center">
          <div className="text-white">
            <div className="w-20 h-20 bg-white/20 rounded-[20px] flex items-center justify-center mb-6 backdrop-blur-sm">
              <span className="text-white font-bold text-4xl">M</span>
            </div>
            <h2 className="text-4xl font-bold mb-4">
              Bienvenido a Mexmo
            </h2>
            <p className="text-white/90 text-lg">
              Viaja por México de forma segura, cómoda y económica. Reserva tus boletos de autobús en minutos.
            </p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-1/2 p-12 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            {/* Title */}
            <h1 className="text-3xl font-bold text-[#111827] mb-2">
              Inicia sesión
            </h1>
            <p className="text-[#6B7280] mb-8">
              Accede a tu cuenta de Mexmo
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <AuthInput
                type="email"
                label="Correo electrónico"
                placeholder="tu@email.com"
                value={email}
                onChange={setEmail}
                icon={<Mail className="w-5 h-5" />}
              />

              <AuthInput
                type="password"
                label="Contraseña"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={setPassword}
              />

              {/* Forgot Password */}
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm font-semibold text-[#FF6A2A] hover:text-[#C2410C] transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                variant="primary"
                className="w-full"
              >
                Iniciar sesión
              </Button>

              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E5E7EB]"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-[#6B7280]">
                    o continúa con
                  </span>
                </div>
              </div>

              {/* Google Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-[14px] border border-[#E5E7EB] bg-white hover:bg-[#F7F8FA] transition-colors font-semibold text-[#111827]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuar con Google
              </button>
            </form>

            {/* Sign Up Link */}
            <div className="text-center mt-8">
              <span className="text-[#6B7280]">¿No tienes cuenta? </span>
              <button className="font-semibold text-[#FF6A2A] hover:text-[#C2410C] transition-colors">
                Crear cuenta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;